import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { WCU } from '@services/wcu';
import { getProductsWithVersions, Product } from '@services/product';
import { updateVersions, UpdateCommandArguments } from '@services/update';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Typography
} from '@mui/material';
import { ActionsContainer, LoaderContainer } from './styles';
import useApi from '@hooks/useApi';
import CircularProgress from '@mui/material/CircularProgress';
import AlertDialog from '@components/AlertDialog';
import PaperComponent from '@components/Paper';

interface WCUPropsDialogProps {
  open: boolean;
  onCloseRequest: () => void;
  wcu: WCU[];
}

export default function UpdateVersionsDialog(props: WCUPropsDialogProps): ReactElement {
  const { open, onCloseRequest, wcu } = props;
  const [updates, setUpdates] = useState<UpdateCommandArguments[]>([]);
  const [isForced, setIsForced] = useState<boolean>(false);
  const products = useApi(getProductsWithVersions);
  const update = useApi(updateVersions);
  const [message, setMessage] = useState<string | ReactElement>('');

  const setDefaults = (): void => {
    setUpdates([]);
    setIsForced(false);
  };

  useEffect(() => {
    if (!open) {
      return setDefaults();
    }
    void products.request();
  }, [open]);

  useEffect(() => {
    if (!update.data) {
      return;
    }
    setMessage(<Typography>{update.data.detail}</Typography>);
  }, [update.data]);

  useEffect(() => {
    if (!update.error) {
      return;
    }
    const msg = <Typography>{update.error}</Typography>;
    setMessage(msg);
  }, [update.error]);

  const onVersionSelect = (productId: string, version: string): void => {
    setUpdates((state) => {
      const index = state.findIndex((u) => u.product === productId);
      if (version === '') {
        if (index !== -1) {
          return state.slice(0, index).concat(state.slice(index + 1));
        }
        return state;
      }
      if (index === -1) {
        return state.concat([
          {
            product: productId,
            version
          }
        ]);
      }
      const newUpdates: UpdateCommandArguments[] = [...state];
      newUpdates[index] = {
        ...state[index],
        version
      };
      return newUpdates;
    });
  };

  const onSharedFileSelect = (productId: string, fileName: string, selected: boolean): void => {
    setUpdates((state) => {
      const index = state.findIndex((u) => u.product === productId);
      if (index === -1) {
        return state;
      }

      const update = state[index];
      const include = selected
        ? [...(update.include ?? []), fileName]
        : (update.include ?? []).filter((file) => file !== fileName);
      const newUpdate: UpdateCommandArguments = {
        product: update.product,
        version: update.version
      };
      if (include.length > 0) {
        newUpdate.include = include;
      }

      const newUpdates = [...state];
      newUpdates[index] = newUpdate;
      return newUpdates;
    });
  };

  const onApply = (): void => {
    void update.request(
      updates,
      wcu.map((wcu) => wcu.wcu_id),
      isForced
    );
    setDefaults();
  };

  const applyEnabled = !update.loading && updates.length > 0;
  const loadingIsActive = products.loading || update.loading;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title">
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
        Update Versions
      </DialogTitle>
      <DialogContent>
        {loadingIsActive && (
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        )}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          {!loadingIsActive && (
            <Fragment>
              {products.data?.map((product: Product) => {
                const selectedUpdate = updates.find((u) => u.product === product.id);
                return (
                  <Grid item key={product.id} xs={12} md={6}>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxSizing: 'border-box',
                        height: '100%',
                        p: 2
                      }}>
                      <Typography sx={{ mb: 1, fontWeight: 500 }}>{product.name}</Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={selectedUpdate?.version ?? ''}
                        onChange={(event: SelectChangeEvent) => {
                          onVersionSelect(product.id, event.target.value);
                        }}>
                        <MenuItem value="">
                          <em>none</em>
                        </MenuItem>
                        {product.versions?.map((version: string) => (
                          <MenuItem key={version} value={version}>
                            {version}
                          </MenuItem>
                        ))}
                      </Select>
                      {product.shared && product.shared.length > 0 && (
                        <FormGroup sx={{ mt: 1 }}>
                          <Typography variant="caption">
                            Include shared files:{' '}
                            <Box component="span" sx={{ color: 'error.main' }}>
                              (select only if required)
                            </Box>
                          </Typography>
                          {product.shared.map((fileName) => (
                            <FormControlLabel
                              key={fileName}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedUpdate?.include?.includes(fileName) ?? false}
                                  disabled={!selectedUpdate}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onSharedFileSelect(product.id, fileName, event.target.checked);
                                  }}
                                />
                              }
                              label={fileName}
                            />
                          ))}
                        </FormGroup>
                      )}
                    </Box>
                  </Grid>
                );
              })}
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>Immediate Update:</Typography>
                <Switch
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setIsForced(event.target.checked);
                  }}
                  checked={isForced}
                />
              </Grid>
            </Fragment>
          )}
        </Grid>
        <AlertDialog
          title="Update Result"
          content={message}
          open={message !== ''}
          onCloseRequest={() => {
            setMessage('');
          }}
        />
      </DialogContent>
      <ActionsContainer>
        <Button sx={{ float: 'left' }} onClick={onCloseRequest} disabled={update.loading}>
          Close
        </Button>
        <Button
          disabled={!applyEnabled}
          sx={{ float: 'right' }}
          variant="contained"
          onClick={onApply}>
          Apply
        </Button>
      </ActionsContainer>
    </Dialog>
  );
}
